// src/Chatbot.js
// Organized by agents: Biz / Agentforce / Lyzr / Make
// (gear dropdown, shimmer typing bubble, direct calls, no Netlify/proxy)

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import './Chatbot.css';
import { ChatContext } from './ChatContext';
import { voiceTurn } from './voice/DeepgramVoice';
import { useStrings } from './strings/StringsContext';
import VideoTextPageDID from './VideoTextPageDID';


/* =========================================================
   Global UI flags / shared helpers
   ========================================================= */
const USE_INLINE_VOICE_UI = true;
const formatLatency = (ms) => `${(ms / 1000).toFixed(2)}s`;

const headers = { 'Content-Type': 'application/json' };
const getTime = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const formatBotReply = (text) => `${text}`;
const genId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const initialMessages = (s, agentName) => ([
{
type: 'meta',
// ✅ from JSON; fallback shows agent name
text: (typeof s?.meta_joined === 'string' && s.meta_joined.trim())
? s.meta_joined
: `${agentName || 'Agent'} joined`,
timestamp: getTime(),
},
{
type: 'bot',
// ✅ from JSON; fallback mentions agent name
text: formatBotReply(
(typeof s?.bot_intro === 'string' && s.bot_intro.trim())
  ? s.bot_intro
  : `Hi! I’m ${agentName || 'Agent'}, an AI agent built in Agentforce that can help your all emergency and critical financial queries and connect you with our Experts if needed. This experience, powered entirely by Agentforce, keeps improving daily! Ask me things like, 'I would like to create a dispute request?.`
  ),
 timestamp: getTime(),
},
{
type: 'bot-buttons',
 buttons: Array.isArray(s?.bot_buttons) ? s.bot_buttons : [],
  timestamp: getTime(),
 },
]);
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:.*;base64,/, ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

async function ensureMicPermission() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}

// Universal reply extractor
function extractReply(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  return (
    data.response ||
    data.message ||
    data.output ||
    data.reply ||
    data.assistant_response ||
    data.assistantResponse?.text ||
    data.choices?.[0]?.message?.content ||
    data.choices?.[0]?.text ||
    data.data?.[0]?.text ||
    ''
  );
}

/* =========================================================
   Upload API
   ========================================================= */
const uploadApiUrl = process.env.REACT_APP_UPLOAD_API_URL;
/* =========================================================
   Agent endpoints / options
   ========================================================= */
const AGENT_ENDPOINTS = {
  'Biz Agent': process.env.REACT_APP_BIZ_API_BASE,
  'Lyzr Agent': 'LYZR_DIRECT',
  'Make Agent': 'MAKE_DIRECT',
};
const AGENT_OPTIONS = ['Agentforce', 'Biz Agent']; // (Make Agent wired, hidden)



/* =========================================================
   Agent: Agentforce (Salesforce OAuth + Flow)
   ========================================================= */
const SF_TOKEN_URL     = process.env.REACT_APP_SF_TOKEN_URL;
const SF_CLIENT_ID     = process.env.REACT_APP_SF_CLIENT_ID;
const SF_CLIENT_SECRET = process.env.REACT_APP_SF_CLIENT_SECRET;


// (keep legacy constants; not used directly in Flow path)
const AGENT_API_BASE = process.env.REACT_APP_AGENT_API_BASE;
const AGENT_ID = process.env.REACT_APP_AGENT_ID;

// Salesforce Flow (Custom Action) endpoint
const SF_FLOW_ENDPOINT = process.env.REACT_APP_SF_FLOW_ENDPOINT;


// Salesforce Biz Flow (Custom Action) endpoint
const SFBIZ_FLOW_ENDPOINT = process.env.REACT_APP_SFBIZ_FLOW_ENDPOINT;

async function fetchSfToken() {
  const body = new URLSearchParams();
  body.append('grant_type', 'client_credentials');
  body.append('client_id', SF_CLIENT_ID);
  body.append('client_secret', SF_CLIENT_SECRET);

  const { data } = await axios.post(SF_TOKEN_URL, body.toString(), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    timeout: 1500000,
  });
  if (!data?.access_token) throw new Error('Salesforce token missing.');
  return data;
}
// async function createAfSession(accessToken) {
//   const url = `${AGENT_API_BASE}/agents/${AGENT_ID}/sessions`;
//   const { data } = await axios.post(url, {}, {
//     headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
//     timeout: 1500000,
//   });
//   const sid = data?.id || data?.sessionId;
//   if (!sid) throw new Error('Agentforce sessionId missing.');
//   return sid;
// }
// async function sendAfMessage(accessToken, sessionId, sequenceId, text) {
//   const url = `${AGENT_API_BASE}/agents/${AGENT_ID}/sessions/${sessionId}/messages`;
//   const payload = { sequenceId, role: 'user', content: text };
//   const { data } = await axios.post(url, payload, {
//     headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
//     timeout: 3000000,
//   });
//   const replyText = extractReply(data);
//   return { replyText: replyText || '', raw: data };
// }
async function endAfSession(accessToken, sessionId) {
  const url = `${AGENT_API_BASE}/agents/${AGENT_ID}/sessions/${sessionId}`;
  await axios.delete(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
    timeout: 1000000,
  });
}

async function sendSfFlowMessage(accessToken, userInput, agentSessionId, displayMode){
const inputs = [{
    userInput,
    channelSource: "WEB",
    channelDisplayMode: displayMode
  }];
  if (agentSessionId) inputs[0].agentSessionId = agentSessionId;

  const payload = { inputs };

  const { data } = await axios.post(SF_FLOW_ENDPOINT, payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    timeout: 3000000,
  });

  const rawResponse = data?.[0]?.outputValues?.generatedAgentResponse;
  const newSessionId = data?.[0]?.outputValues?.generatedSessionId || null;

  let extractedMessage = '';
  try {
    if (rawResponse) {
      const parsedResponse = JSON.parse(rawResponse);
      extractedMessage = parsedResponse?.value?.message || '';
    }
  } catch {
    // ignore parse issues
  }

  const replyText =
    extractReply(data) ||
    extractReply(Array.isArray(data) ? data[0] : null) ||
    extractReply(data?.outputs) ||
    extractReply(data?.results) ||
    '';

  return {
    replyText: replyText || extractedMessage || '',
    sessionId: newSessionId || agentSessionId || null,
  };
}
/* Agent Biz Flow */
async function sendSfBizFlowMessage(accessToken, userInput, agentSessionId, displayMode) {
const inputs = [{
    userInput,
    channelSource: "WEB",
    channelDisplayMode: displayMode
  }];
  if (agentSessionId) inputs[0].agentSessionId = agentSessionId;

  const payload = { inputs };
  console.log("payload", JSON.stringify(payload) );
  const { data } = await axios.post(SFBIZ_FLOW_ENDPOINT, payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    timeout: 3000000,
  });

  const rawResponse = data?.[0]?.outputValues?.generatedAgentResponse;
  console.log("rawResponse",rawResponse );


  const newSessionId = data?.[0]?.outputValues?.generatedSessionId || null;
  console.log("newSessionId", newSessionId );
  let extractedMessage = '';
  try {
    if (rawResponse) {
      //const parsedResponse = JSON.parse(rawResponse);
      //extractedMessage = parsedResponse?.value?.message || '';
      const message = rawResponse.split('"message":"')[1].split('"}}')[0];
      console.log("Extracted message:\n", message);
      extractedMessage = message || '';
      console.log("extractedMessage",extractedMessage);
      //console.log("parsedResponse",parsedResponse );
    }
  } catch {
    // ignore parse issues
  }

  const replyText =
    extractReply(data) ||
    extractReply(Array.isArray(data) ? data[0] : null) ||
    extractReply(data?.outputs) ||
    extractReply(data?.results) ||
    '';

  return {
    replyText: replyText || extractedMessage || '',
    sessionId: newSessionId || agentSessionId || null,
  };
}



/* =========================================================
   Agent: Lyzr – direct API
   ========================================================= */
const LYZR_API_URL         = process.env.REACT_APP_LYZR_API_URL;
const LYZR_API_KEY         = process.env.REACT_APP_LYZR_API_KEY;
const LYZR_DEFAULT_USER_ID = process.env.REACT_APP_LYZR_DEFAULT_USER_ID;
const LYZR_AGENT_ID        = process.env.REACT_APP_LYZR_AGENT_ID;


async function sendLyzrMessage({ message, sessionId, userId = LYZR_DEFAULT_USER_ID }) {
  const payload = { user_id: userId, agent_id: LYZR_AGENT_ID, session_id: sessionId, message };
  const { data } = await axios.post(LYZR_API_URL, payload, {
    headers: { 'Content-Type': 'application/json', 'x-api-key': LYZR_API_KEY },
    timeout: 8000000, withCredentials: false,
  });
  const replyText = extractReply(data);
  return { replyText: replyText || '', raw: data };
}

/* =========================================================
   Agent: Make – direct API
   ========================================================= */
const MAKE_BASE     = process.env.REACT_APP_MAKE_BASE;
const MAKE_TEAM_ID  = process.env.REACT_APP_MAKE_TEAM_ID;
const MAKE_AGENT_ID = process.env.REACT_APP_MAKE_AGENT_ID;
const MAKE_TOKEN    = process.env.REACT_APP_MAKE_TOKEN;

async function sendMakeMessage({ message, threadId }) {
  const url = `${MAKE_BASE}/agents/${MAKE_AGENT_ID}/run?teamId=${encodeURIComponent(MAKE_TEAM_ID)}`;
  const payload = { messages: [{ role: 'user', content: message }], threadId: String(threadId || 'default'), config: {} };
  const { data } = await axios.post(url, payload, {
    headers: { 'Content-Type': 'application/json', Authorization: `Token ${MAKE_TOKEN}` },
    timeout: 3000000, withCredentials: false,
  });
  const replyText = extractReply(data) ||
    (Array.isArray(data?.messages) ? data.messages.find(m => m.role !== 'user')?.content : '');
  return { replyText: replyText || '', raw: data };
}

/* =========================================================
   ElevenLabs ConvAI helpers (UI containment/anti-floaters)
   ========================================================= */
const ELEVEN_AGENT_ID   = process.env.REACT_APP_ELEVEN_AGENT_ID;
const ELEVEN_SCRIPT_SRC = process.env.REACT_APP_ELEVEN_SCRIPT_SRC;
   

function injectElevenHardKillCSS() {
  if (document.getElementById('elevenlabs-hardkill-css')) return;
  const st = document.createElement('style');
  st.id = 'elevenlabs-hardkill-css';
  st.textContent = `
    elevenlabs-convai-launcher,
    #elevenlabs-convai-launcher,
    .elevenlabs-convai-launcher,
    .elevenlabs__launcher,
    .elevenlabs__launcher__button,
    .elevenlabs__launcher__backdrop,
    [data-e11n="convai-launcher"],
    [data-e11n="convai-backdrop"],
    iframe[src*="elevenlabs"][src*="launcher"] {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
      position: static !important;
      inset: auto !important;
    }
  `;
  document.head.appendChild(st);
}
function removeElevenFloaters() {
  const sels = [
    'elevenlabs-convai-launcher',
    '#elevenlabs-convai-launcher',
    '.elevenlabs-convai-launcher',
    '.elevenlabs__launcher',
    '.elevenlabs__launcher__button',
    '.elevenlabs__launcher__backdrop',
    '[data-e11n="convai-launcher"]',
    '[data-e11n="convai-backdrop"]',
    'iframe[src*="elevenlabs"][src*="launcher"]'
  ];
  sels.forEach(s => document.querySelectorAll(s).forEach(n => { try { n.remove(); } catch {} }));
}
function ensureElevenScript() {
  return new Promise((resolve, reject) => {
    const markReady = () => { injectElevenHardKillCSS(); removeElevenFloaters(); resolve(); };
    if (window?.customElements?.get?.('elevenlabs-convai')) { markReady(); return; }
    const existing = document.querySelector(`script[src="${ELEVEN_SCRIPT_SRC}"]`);
    if (existing) { existing.addEventListener('load', markReady, { once: true }); return; }
    const s = document.createElement('script');
    s.src = ELEVEN_SCRIPT_SRC;
    s.async = true;
    s.onload = markReady;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}
function adoptElevenFloatersInto(host) {
  if (!host) return;
  const sels = [
    'elevenlabs-convai-launcher',
    '#elevenlabs-convai-launcher',
    '.elevenlabs-convai-launcher',
    '.elevenlabs__launcher',
    '[data-e11n="convai-launcher"]'
  ];
  sels
    .flatMap(s => Array.from(document.querySelectorAll(s)))
    .forEach(node => {
      try {
        host.appendChild(node);
        Object.assign(node.style, {
          position: 'static',
          inset: 'auto',
          bottom: 'auto',
          right: 'auto',
          left: 'auto',
          top: 'auto',
          margin: 0,
          display: 'none'
        });
      } catch {}
    });
}

/* =========================================================
   Icons
   ========================================================= */
const DictateIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="#000" aria-hidden="true">
    <path d="M12 14a3 3 0 0 0 3-3V7a3 3 0 1 0-6 0v4a3 3 0 0 0 3 3z" />
    <path d="M19 11a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.92V20H9a1 1 0 0 0 0 2h6a1 1 0 0 0 0-2h-2v-2.08A7 7 0 0 0 19 11z" />
  </svg>
);
const AttachIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M16.5,6.5v9a4.5,4.5,0,0,1-9,0v-10a3,3,0,0,1,6,0v9a1.5,1.5,0,0,1-3,0v-8" stroke="#000" strokeWidth="2"/>
  </svg>
);
const GearIcon = ({ size = 20 }) => (

<svg
viewBox="0 0 24 24"
width="20"
height="20"
fill="none"
xmlns="http://www.w3.org/2000/svg"
aria-hidden="true"
>
<path
d="M12 8.00002C9.79085 8.00002 7.99999 9.79088 7.99999 12C7.99999 14.2092 9.79085 16 12 16C14.2091 16 16 14.2092 16 12C16 9.79088 14.2091 8.00002 12 8.00002ZM9.99999 12C9.99999 10.8955 10.8954 10 12 10C13.1046 10 14 10.8955 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 9.99999 13.1046 9.99999 12Z"
fill="#0F1729"
fillRule="evenodd"
clipRule="evenodd"
/>
<path
d="M12 8.00002C9.79085 8.00002 7.99999 9.79088 7.99999 12C7.99999 14.2092 9.79085 16 12 16C14.2091 16 16 14.2092 16 12C16 9.79088 14.2091 8.00002 12 8.00002ZM9.99999 12C9.99999 10.8955 10.8954 10 12 10C13.1046 10 14 10.8955 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 9.99999 13.1046 9.99999 12Z"
fill="#0F1729"
fillRule="evenodd"
clipRule="evenodd"
/>
<path
d="M10.7673 1.01709C10.9925 0.999829 11.2454 0.99993 11.4516 1.00001L12.5484 1.00001C12.7546 0.99993 13.0075 0.999829 13.2327 1.01709C13.4989 1.03749 13.8678 1.08936 14.2634 1.26937C14.7635 1.49689 15.1915 1.85736 15.5007 2.31147C15.7454 2.67075 15.8592 3.0255 15.9246 3.2843C15.9799 3.50334 16.0228 3.75249 16.0577 3.9557L16.1993 4.77635L16.2021 4.77788C16.2369 4.79712 16.2715 4.81659 16.306 4.8363L16.3086 4.83774L17.2455 4.49865C17.4356 4.42978 17.6693 4.34509 17.8835 4.28543C18.1371 4.2148 18.4954 4.13889 18.9216 4.17026C19.4614 4.20998 19.9803 4.39497 20.4235 4.70563C20.7734 4.95095 21.0029 5.23636 21.1546 5.4515C21.2829 5.63326 21.4103 5.84671 21.514 6.02029L22.0158 6.86003C22.1256 7.04345 22.2594 7.26713 22.3627 7.47527C22.4843 7.7203 22.6328 8.07474 22.6777 8.52067C22.7341 9.08222 22.6311 9.64831 22.3803 10.1539C22.1811 10.5554 21.9171 10.8347 21.7169 11.0212C21.5469 11.1795 21.3428 11.3417 21.1755 11.4746L20.5 12L21.1755 12.5254C21.3428 12.6584 21.5469 12.8205 21.7169 12.9789C21.9171 13.1653 22.1811 13.4446 22.3802 13.8461C22.631 14.3517 22.7341 14.9178 22.6776 15.4794C22.6328 15.9253 22.4842 16.2797 22.3626 16.5248C22.2593 16.7329 22.1255 16.9566 22.0158 17.14L21.5138 17.9799C21.4102 18.1535 21.2828 18.3668 21.1546 18.5485C21.0028 18.7637 20.7734 19.0491 20.4234 19.2944C19.9803 19.6051 19.4613 19.7901 18.9216 19.8298C18.4954 19.8612 18.1371 19.7852 17.8835 19.7146C17.6692 19.6549 17.4355 19.5703 17.2454 19.5014L16.3085 19.1623L16.306 19.1638C16.2715 19.1835 16.2369 19.2029 16.2021 19.2222L16.1993 19.2237L16.0577 20.0443C16.0228 20.2475 15.9799 20.4967 15.9246 20.7157C15.8592 20.9745 15.7454 21.3293 15.5007 21.6886C15.1915 22.1427 14.7635 22.5032 14.2634 22.7307C13.8678 22.9107 13.4989 22.9626 13.2327 22.983C13.0074 23.0002 12.7546 23.0001 12.5484 23H11.4516C11.2454 23.0001 10.9925 23.0002 10.7673 22.983C10.5011 22.9626 10.1322 22.9107 9.73655 22.7307C9.23648 22.5032 8.80849 22.1427 8.49926 21.6886C8.25461 21.3293 8.14077 20.9745 8.07542 20.7157C8.02011 20.4967 7.97723 20.2475 7.94225 20.0443L7.80068 19.2237L7.79791 19.2222C7.7631 19.2029 7.72845 19.1835 7.69396 19.1637L7.69142 19.1623L6.75458 19.5014C6.5645 19.5702 6.33078 19.6549 6.11651 19.7146C5.86288 19.7852 5.50463 19.8611 5.07841 19.8298C4.53866 19.7901 4.01971 19.6051 3.57654 19.2944C3.2266 19.0491 2.99714 18.7637 2.84539 18.5485C2.71718 18.3668 2.58974 18.1534 2.4861 17.9798L1.98418 17.14C1.87447 16.9566 1.74067 16.7329 1.63737 16.5248C1.51575 16.2797 1.36719 15.9253 1.32235 15.4794C1.26588 14.9178 1.36897 14.3517 1.61976 13.8461C1.81892 13.4446 2.08289 13.1653 2.28308 12.9789C2.45312 12.8205 2.65717 12.6584 2.82449 12.5254L3.47844 12.0054V11.9947L2.82445 11.4746C2.65712 11.3417 2.45308 11.1795 2.28304 11.0212C2.08285 10.8347 1.81888 10.5554 1.61972 10.1539C1.36893 9.64832 1.26584 9.08224 1.3223 8.52069C1.36714 8.07476 1.51571 7.72032 1.63732 7.47528C1.74062 7.26715 1.87443 7.04347 1.98414 6.86005L2.48605 6.02026C2.58969 5.84669 2.71714 5.63326 2.84534 5.45151C2.9971 5.23637 3.22655 4.95096 3.5765 4.70565C4.01966 4.39498 4.53862 4.20999 5.07837 4.17027C5.50458 4.1389 5.86284 4.21481 6.11646 4.28544C6.33072 4.34511 6.56444 4.4298 6.75451 4.49867L7.69141 4.83775L7.69394 4.8363C7.72844 4.8166 7.7631 4.79712 7.79791 4.77788L7.80068 4.77635L7.94225 3.95571C7.97723 3.7525 8.02011 3.50334 8.07542 3.2843C8.14077 3.0255 8.25461 2.67075 8.49926 2.31147C8.80849 1.85736 9.23648 1.49689 9.73655 1.26937C10.1322 1.08936 10.5011 1.03749 10.7673 1.01709ZM14.0938 4.3363C14.011 3.85634 13.9696 3.61637 13.8476 3.43717C13.7445 3.2858 13.6019 3.16564 13.4352 3.0898C13.2378 3.00002 12.9943 3.00002 12.5073 3.00002H11.4927C11.0057 3.00002 10.7621 3.00002 10.5648 3.0898C10.3981 3.16564 10.2555 3.2858 10.1524 3.43717C10.0304 3.61637 9.98895 3.85634 9.90615 4.3363L9.75012 5.24064C9.69445 5.56333 9.66662 5.72467 9.60765 5.84869C9.54975 5.97047 9.50241 6.03703 9.40636 6.13166C9.30853 6.22804 9.12753 6.3281 8.76554 6.52822C8.73884 6.54298 8.71227 6.55791 8.68582 6.57302C8.33956 6.77078 8.16643 6.86966 8.03785 6.90314C7.91158 6.93602 7.83293 6.94279 7.70289 6.93196C7.57049 6.92094 7.42216 6.86726 7.12551 6.7599L6.11194 6.39308C5.66271 6.2305 5.43809 6.14921 5.22515 6.16488C5.04524 6.17811 4.87225 6.23978 4.72453 6.34333C4.5497 6.46589 4.42715 6.67094 4.18206 7.08103L3.72269 7.84965C3.46394 8.2826 3.33456 8.49907 3.31227 8.72078C3.29345 8.90796 3.32781 9.09665 3.41141 9.26519C3.51042 9.4648 3.7078 9.62177 4.10256 9.9357L4.82745 10.5122C5.07927 10.7124 5.20518 10.8126 5.28411 10.9199C5.36944 11.036 5.40583 11.1114 5.44354 11.2504C5.47844 11.379 5.47844 11.586 5.47844 12C5.47844 12.414 5.47844 12.621 5.44354 12.7497C5.40582 12.8887 5.36944 12.9641 5.28413 13.0801C5.20518 13.1875 5.07927 13.2876 4.82743 13.4879L4.10261 14.0643C3.70785 14.3783 3.51047 14.5352 3.41145 14.7349C3.32785 14.9034 3.29349 15.0921 3.31231 15.2793C3.33461 15.501 3.46398 15.7174 3.72273 16.1504L4.1821 16.919C4.4272 17.3291 4.54974 17.5342 4.72457 17.6567C4.8723 17.7603 5.04528 17.8219 5.2252 17.8352C5.43813 17.8508 5.66275 17.7695 6.11199 17.607L7.12553 17.2402C7.42216 17.1328 7.5705 17.0791 7.7029 17.0681C7.83294 17.0573 7.91159 17.064 8.03786 17.0969C8.16644 17.1304 8.33956 17.2293 8.68582 17.427C8.71228 17.4421 8.73885 17.4571 8.76554 17.4718C9.12753 17.6719 9.30853 17.772 9.40635 17.8684C9.50241 17.963 9.54975 18.0296 9.60765 18.1514C9.66662 18.2754 9.69445 18.4367 9.75012 18.7594L9.90615 19.6637C9.98895 20.1437 10.0304 20.3837 10.1524 20.5629C10.2555 20.7142 10.3981 20.8344 10.5648 20.9102C10.7621 21 11.0057 21 11.4927 21H12.5073C12.9943 21 13.2378 21 13.4352 20.9102C13.6019 20.8344 13.7445 20.7142 13.8476 20.5629C13.9696 20.3837 14.011 20.1437 14.0938 19.6637L14.2499 18.7594C14.3055 18.4367 14.3334 18.2754 14.3923 18.1514C14.4502 18.0296 14.4976 17.963 14.5936 17.8684C14.6915 17.772 14.8725 17.6719 15.2344 17.4718C15.2611 17.4571 15.2877 17.4421 15.3141 17.427C15.6604 17.2293 15.8335 17.1304 15.9621 17.0969C16.0884 17.064 16.167 17.0573 16.2971 17.0681C16.4295 17.0791 16.5778 17.1328 16.8744 17.2402L17.888 17.607C18.3372 17.7696 18.5619 17.8509 18.7748 17.8352C18.9547 17.8219 19.1277 17.7603 19.2754 17.6567C19.4502 17.5342 19.5728 17.3291 19.8179 16.919L20.2773 16.1504C20.536 15.7175 20.6654 15.501 20.6877 15.2793C20.7065 15.0921 20.6721 14.9034 20.5885 14.7349C20.4895 14.5353 20.2921 14.3783 19.8974 14.0643L19.1726 13.4879C18.9207 13.2876 18.7948 13.1875 18.7159 13.0801C18.6306 12.9641 18.5942 12.8887 18.5564 12.7497C18.5215 12.6211 18.5215 12.414 18.5215 12C18.5215 11.586 18.5215 11.379 18.5564 11.2504C18.5942 11.1114 18.6306 11.036 18.7159 10.9199C18.7948 10.8126 18.9207 10.7124 19.1725 10.5122L19.8974 9.9357C20.2922 9.62176 20.4896 9.46479 20.5886 9.26517C20.6722 9.09664 20.7065 8.90795 20.6877 8.72076C20.6654 8.49906 20.5361 8.28259 20.2773 7.84964L19.8179 7.08102C19.5728 6.67093 19.4503 6.46588 19.2755 6.34332C19.1277 6.23977 18.9548 6.1781 18.7748 6.16486C18.5619 6.14919 18.3373 6.23048 17.888 6.39307L16.8745 6.75989C16.5778 6.86725 16.4295 6.92093 16.2971 6.93195C16.167 6.94278 16.0884 6.93601 15.9621 6.90313C15.8335 6.86965 15.6604 6.77077 15.3142 6.57302C15.2877 6.55791 15.2611 6.54298 15.2345 6.52822C14.8725 6.3281 14.6915 6.22804 14.5936 6.13166C14.4976 6.03703 14.4502 5.97047 14.3923 5.84869C14.3334 5.72467 14.2499 5.56332 14.0938 5.24064Z"
fill="#0F1729"
fillRule="evenodd"
clipRule="evenodd"
/>
</svg>
);
const ClearChatIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
    <g fill="currentColor">
      <path d="M6 8.711c-1.224 0-2.288.443-2.986 1.201C2.315 10.671 2 11.682 2 12.711v21.771c0 2.2 1.8 4.002 4 4h3v4.59c0 .668.425 1.249.963 1.493a1.67 1.67 0 0 0 1.758-.258l6.654-5.825H36c2.199 0 4-1.8 4-4v-8.771a1 1 0 0 0-1-1 1 1 0 0 0-1 1v8.771c0 1.126-.875 2-2 2H18a1 1 0 0 0-.658.249L11 42.279v-4.797a1 1 0 0 0-1-1H6c-1.124.001-2-.874-2-2V12.711c0-.627.185-1.116.486-1.443.302-.328.738-.557 1.514-.557h18a1 1 0 0 0 1-1 1 1 0 0 0-1-1z" />
      <path d="M36.26 3.293c-5.367 0-9.739 4.373-9.739 9.74 0 5.368 4.372 9.74 9.739 9.74S46 18.401 46 13.033c0-5.367-4.373-9.74-9.74-9.74zm0 2a7.725 7.725 0 0 1 7.74 7.74c0 4.287-3.454 7.74-7.74 7.74s-7.739-3.453-7.739-7.74a7.724 7.724 0 0 1 7.739-7.74z" />
      <path d="M36.26 8.287a1 1 0 0 0-1 1v2.746h-2.746a1 1 0 0 0-1 1 1 1 0 0 0 1 1h2.746v2.746a1 1 0 0 0 1 1 1 1 0 0 0 1-1v-2.746h2.746a1 1 0 0 0 1-1 1 1 0 0 0-1-1H37.26V9.287a1 1 0 0 0-1-1zM9 28.904a1 1 0 0 0-1 1 1 1 0 0 0 1 1h14a1 1 0 0 0 1-1 1 1 0 0 0-1-1zM9 22.904a1 1 0 0 0-1 1 1 1 0 0 0 1 1h19a1 1 0 0 0 1-1 1 1 0 0 0-1-1zM9 16.904a1 1 0 0 0-1 1 1 1 0 0 0 1 1h14a1 1 0 0 0 1-1 1 1 0 0 0-1-1z" />
    </g>
  </svg>
);

 const ElevenIcon = ({ size = 20, title = 'Call', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title}
    width={size}
    height={size}
    viewBox="0 0 473.806 473.806"
    fill="currentColor"
    {...props}
  >
    <title>{title}</title>
    <path d="M374.456 293.506c-9.7-10.1-21.4-15.5-33.8-15.5-12.3 0-24.1 5.3-34.2 15.4l-31.6 31.5c-2.6-1.4-5.2-2.7-7.7-4-3.6-1.8-7-3.5-9.9-5.3-29.6-18.8-56.5-43.3-82.3-75-12.5-15.8-20.9-29.1-27-42.6 8.2-7.5 15.8-15.3 23.2-22.8 2.8-2.8 5.6-5.7 8.4-8.5 21-21 21-48.2 0-69.2l-27.3-27.3c-3.1-3.1-6.3-6.3-9.3-9.5-6-6.2-12.3-12.6-18.8-18.6-9.7-9.6-21.3-14.7-33.5-14.7s-24 5.1-34 14.7l-.2.2-34 34.3c-12.8 12.8-20.1 28.4-21.7 46.5-2.4 29.2 6.2 56.4 12.8 74.2 16.2 43.7 40.4 84.2 76.5 127.6 43.8 52.3 96.5 93.6 156.7 122.7 23 10.9 53.7 23.8 88 26 2.1.1 4.3.2 6.3.2 23.1 0 42.5-8.3 57.7-24.8.1-.2.3-.3.4-.5 5.2-6.3 11.2-12 17.5-18.1 4.3-4.1 8.7-8.4 13-12.9 9.9-10.3 15.1-22.3 15.1-34.6 0-12.4-5.3-24.3-15.4-34.3l-54.9-55.1zm35.8 105.3c-.1 0-.1.1 0 0-3.9 4.2-7.9 8-12.2 12.2-6.5 6.2-13.1 12.7-19.3 20-10.1 10.8-22 15.9-37.6 15.9-1.5 0-3.1 0-4.6-.1-29.7-1.9-57.3-13.5-78-23.4-56.6-27.4-106.3-66.3-147.6-115.6-34.1-41.1-56.9-79.1-72-119.9-9.3-24.9-12.7-44.3-11.2-62.6 1-11.7 5.5-21.4 13.8-29.7l34.1-34.1c4.9-4.6 10.1-7.1 15.2-7.1 6.3 0 11.4 3.8 14.6 7l.3.3c6.1 5.7 11.9 11.6 18 17.9 3.1 3.2 6.3 6.4 9.5 9.7l27.3 27.3c10.6 10.6 10.6 20.4 0 31-2.9 2.9-5.7 5.8-8.6 8.6-8.4 8.6-16.4 16.6-25.1 24.4-.2.2-.4.3-.5.5-8.6 8.6-7 17-5.2 22.7l.3.9c7.1 17.2 17.1 33.4 32.3 52.7l.1.1c27.6 34 56.7 60.5 88.8 80.8 4.1 2.6 8.3 4.7 12.3 6.7 3.6 1.8 7 3.5 9.9 5.3.4.2.8.5 1.2.7 3.4 1.7 6.6 2.5 9.9 2.5 8.3 0 13.5-5.2 15.2-6.9l34.2-34.2c3.4-3.4 8.8-7.5 15.1-7.5 6.2 0 11.3 3.9 14.4 7.3l.2.2 55.1 55.1c10.3 10.2 10.3 20.7.1 31.3zM256.056 112.706c26.2 4.4 50 16.8 69 35.8s31.3 42.8 35.8 69c1.1 6.6 6.8 11.2 13.3 11.2.8 0 1.5-.1 2.3-.2 7.4-1.2 12.3-8.2 11.1-15.6-5.4-31.7-20.4-60.6-43.3-83.5s-51.8-37.9-83.5-43.3c-7.4-1.2-14.3 3.7-15.6 11s3.5 14.4 10.9 15.6zM473.256 209.006c-8.9-52.2-33.5-99.7-71.3-137.5s-85.3-62.4-137.5-71.3c-7.3-1.3-14.2 3.7-15.5 11-1.2 7.4 3.7 14.3 11.1 15.6 46.6 7.9 89.1 30 122.9 63.7 33.8 33.8 55.8 76.3 63.7 122.9 1.1 6.6 6.8 11.2 13.3 11.2.8 0 1.5-.1 2.3-.2 7.3-1.1 12.3-8.1 11-15.4z"/>
  </svg>
);

// RecorderButtonPublic.jsx
const RecorderButtonPublic = ({ size = 20, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open video assistant"
      style={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        display: 'inline-block',
        width: size,
        height: size,
      }}
    >
      <img
        src="/cam-recorder.png"
        alt="Recorder"
        width={size}
        height={size}
        style={{ display: 'block' }}
      />
    </button>
  );
};
/* =========================================================
   Shimmer typing bubble
   ========================================================= */
const ShimmerMessage = ({ brand, agentName, theme }) => (
  <div className="chatbot-message bot">
    <div className="bot-avatar-wrapper">
      <img
        src={brand?.logoSrc || '/bot-avatar.png'}
        className="avatar"
        alt={brand?.botName || agentName || 'Bot'}
      />
    </div>

    <div
      className="shimmer-bubble"
      style={{
        background: theme?.botBubbleBg || '#f3f4f6',
        borderColor: theme?.borderColor || '#e5e7eb'
      }}
    >
      <div className="shimmer-bar shimmer-bar-1"></div>
      <div className="shimmer-bar shimmer-bar-2"></div>
      <div className="shimmer-bar shimmer-bar-3"></div>
    </div>

    <div className="bot-timestamp-left">&nbsp;</div>
  </div>
);

/* =========================================================
   Component
   ========================================================= */
const Chatbot = () => {
const { strings, agentName, brand, theme } = useStrings();  // URL param behavior
  const params = new URLSearchParams(window.location.search);
  const agentParam = params.get('agent');

const getDisplayMode = useCallback(() => {
  if (agentParam === '2') return 'FULLSCREEN';
  if (agentParam === '1') return 'MINIMIZED';
  return 'EMBEDDED';
}, [agentParam]);


  const isAgent2 = agentParam === '2';

  const openFromQuery   = agentParam === '2';
  const expandFromQuery = agentParam === '2';

//togetctype
  const getCType = useCallback(() => {
  if (agentParam === '1') return 'AGENT1';
  if (agentParam === '2') return 'AGENT2';

  const isMobileDevice =
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
    window.innerWidth <= 768;

  if (isMobileDevice) return 'MOBILE';
  return 'WEB';
}, [agentParam]);
  //const suppressIntro   = agentParam === '2';

  // ChatContext (guard if not mounted)
  const safeCtx = useContext(ChatContext) || {};
  const { triggeredPrompt = '', setTriggeredPrompt = () => {} } = safeCtx;





// show/hide the D-ID video page overlay
const [showVideoPage, setShowVideoPage] = useState(false);



  // UI state
  const [isOpen, setIsOpen] = useState(openFromQuery);
  const [isExpanded, setIsExpanded] = useState(expandFromQuery);
const [messages, setMessages] = useState([]);
useEffect(() => {
  if (!strings) return;

  setMessages(prev => {
    if (!prev.length) {
      return initialMessages(strings, agentName);
    }

    // 🔥 Replace ONLY intro message dynamically
    return prev.map(msg => {
      if (msg.type === 'meta') {
        return {
          ...msg,
          text: strings.meta_joined || `${agentName} joined`
        };
      }

      if (msg.type === 'bot' && msg.text?.includes('Hi')) {
        return {
          ...msg,
          text: strings.bot_intro || msg.text
        };
      }

      return msg;
    });
  });

}, [strings, agentName]);
const [input, setInput] = useState('');
  const [rows, setRows] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasVoiceCtrl, setHasVoiceCtrl] = useState(false);
// one-line until typing (only for agent=2)

  // Voice (Deepgram) overlay state
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voicePaused, setVoicePaused] = useState(false);
  const voiceCtrlRef = useRef(null);

  // ElevenLabs inline widget state
  const [elevenOpen, setElevenOpen] = useState(false);
  const [elevenReady, setElevenReady] = useState(false);
  const [elevenError, setElevenError] = useState('');
  const elevenHostRef = useRef(null);
  const elevenFloatRef = useRef(null);
  const elevenMORef = useRef(null);
  const elevenIntervalRef = useRef(null);
  const elevenElRef = useRef(null);

  // Agent session state
  const [conversationId, setConversationId] = useState(localStorage.getItem('conversationId') || null);

  // Biz Agent client session
  const [bizSessionId, setBizSessionId] = useState(localStorage.getItem('bizSessionId') || null);

  // Lyzr
  const [lyzrSessionId, setLyzrSessionId] = useState(localStorage.getItem('lyzrSessionId') || null);

  // Make
  const [makeThreadId, setMakeThreadId] = useState(localStorage.getItem('makeThreadId') || null);

  // Agentforce session/token
  const [afAuth, setAfAuth] = useState({ sessionId: null });
  const afAccessTokenRef = useRef(null);
  const afSeqRef = useRef(1);
  const [sfAgentSessionId, setSfAgentSessionId] = useState(localStorage.getItem('sfAgentSessionId') || null);

  // Agent selection
  const saved = localStorage.getItem('selectedAgent');
  const allowed = new Set(['Agentforce', 'Biz Agent', 'Lyzr Agent']);
  const baseAgent = saved === 'Lyzr Agent' ? 'Agentforce' : (saved || 'Agentforce');
const [selectedAgent, setSelectedAgent] = useState(baseAgent);
  const [apiBase, setApiBase] = useState(AGENT_ENDPOINTS[baseAgent]);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const settingsRef = useRef(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  /* ---------- Global effects ---------- */
/* branding from json*/
  useEffect(() => {
    const root = document.documentElement;
    if (!theme) return;

    root.style.setProperty('--primary-color', theme.primaryColor || '#0B5CAB');
    root.style.setProperty('--secondary-color', theme.secondaryColor || '#F4F7FB');
    root.style.setProperty('--header-bg', theme.headerBg || '#0B5CAB');
    root.style.setProperty('--header-text', theme.headerText || '#FFFFFF');
    root.style.setProperty('--bot-bubble-bg', theme.botBubbleBg || '#EAF3FF');
    root.style.setProperty('--user-bubble-bg', theme.userBubbleBg || '#DCF8C6');
    root.style.setProperty('--button-bg', theme.buttonBg || '#0B5CAB');
    root.style.setProperty('--button-text', theme.buttonText || '#FFFFFF');
    root.style.setProperty('--page-bg', theme.pageBg || '#FFFFFF');
    root.style.setProperty('--text-color', theme.textColor || '#1F1F1F');
    root.style.setProperty('--border-color', theme.borderColor || '#D9E2EC');
  }, [theme]);


  useEffect(() => {
    injectElevenHardKillCSS();
    removeElevenFloaters();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, isUploading]);

useEffect(() => {
  const el = textareaRef.current;
  if (!el) return;

  const ONE = isExpanded ? 36 : 32;     // one-line height
  const MAX = isExpanded ? 280 : 180;   // your existing clamp

  // agent=2 + empty -> lock to one line
  if (isAgent2 && input.trim().length === 0) {
    el.style.height = '60px';
    el.style.overflowY = 'scroll';
    setRows(1);
    return;
  }

  // normal auto-grow (or agent=2 after typing)
  el.style.height = 'auto';
  if (isExpanded) {
    const next = Math.min(el.scrollHeight || ONE, MAX);
    el.style.height = `${Math.max(ONE, next)}px`;
    setRows(1);
  } else {
    const newlineRows = (input.match(/\n/g) || []).length + 1;
    setRows(Math.min(Math.max(newlineRows, 1), 6));
  }
}, [input, isExpanded, voiceOpen, elevenOpen, isAgent2]);

  useEffect(() => {
    localStorage.setItem('selectedAgent', selectedAgent);
    setApiBase(AGENT_ENDPOINTS[selectedAgent] || AGENT_ENDPOINTS['Biz Agent']);
  }, [selectedAgent]);

  useEffect(() => {
    const onClick = (e) => { if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowAgentDropdown(false); };
    const onEsc = (e) => e.key === 'Escape' && setShowAgentDropdown(false);
    if (showAgentDropdown) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onEsc);
    }
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showAgentDropdown]);

  /* ---------- Context trigger ---------- */
  useEffect(() => {
    if (triggeredPrompt) {
      const t = getTime();
      setMessages((p) => [...p, { type: 'user', text: triggeredPrompt, timestamp: t }]);
      routeMessage(triggeredPrompt);
      setTriggeredPrompt('');
      setIsOpen(true);
      setIsExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggeredPrompt]);

  /* ---------- Agentforce token prefetch ---------- */
  const getSfAccessTokenOnce = useCallback(async () => {
    if (afAccessTokenRef.current) return afAccessTokenRef.current;
    const tok = await fetchSfToken();
    afAccessTokenRef.current = tok.access_token;
    return afAccessTokenRef.current;
  }, []);

  useEffect(() => {
    if (selectedAgent === 'Agentforce') {
      (async () => {
        try {
          if (!afAccessTokenRef.current) {
            await getSfAccessTokenOnce();
          }
        } catch (e) {
          console.error('SF token prefetch failed:', e);
        }
      })();
    }
  }, [selectedAgent, getSfAccessTokenOnce]);

  /* =========================================================
     Biz Agent: handler (api_token in body + per-chat session)
     ========================================================= */
  const handleBizResponse = useCallback(
  async (userMessage) => {
    setIsLoading(true);
    try {
      // ensure Salesforce OAuth token (reuse the same helper/ref used by Agentforce)
      if (!afAccessTokenRef.current) {
        await getSfAccessTokenOnce();
      }

      // Current/previous Biz session
      const sid = localStorage.getItem('bizSessionId');
      console.log('Using Biz Session ID:', sid);

      // measure latency
      const startMs = performance.now();

      // call Biz Flow (same style as Agentforce)
    const { replyText, sessionId } = await sendSfBizFlowMessage(
  afAccessTokenRef.current,
  userMessage,
  sid,
  getDisplayMode()
);

      // accept server-returned session id, if it changes
      if (sessionId && sessionId !== bizSessionId) {
        setBizSessionId(sessionId);
        localStorage.setItem('bizSessionId', sessionId);
      }
      console.log('Biz Session ID now:', sessionId);

      const botReply = formatBotReply(replyText || strings.default_reply);
      const elapsed = performance.now() - startMs;

      setMessages((prev) => [
        ...prev,
        { type: 'bot', text: botReply, timestamp: `${getTime()} (${formatLatency(elapsed)})` },
      ]);
      return botReply;
    } catch (e) {
      console.error('Biz Flow error:', e?.response?.status, e?.response?.data || e.message);
      const msg = formatBotReply(strings.error_response);
      setMessages((prev) => [
        ...prev,
        { type: 'bot', text: msg, timestamp: `${getTime()} (${formatLatency(0)})` },
      ]);
      return msg;
    } finally {
      setIsLoading(false);
    }
  },
  [bizSessionId, getSfAccessTokenOnce, strings?.default_reply, strings?.error_response]
);


  /* =========================================================
     Agentforce helpers (ensure token only; session handled by Flow)
     ========================================================= */
  const ensureAfSession = useCallback(async () => {
    // legacy structure; now we only ensure token (no session)
    if (!afAccessTokenRef.current) {
      const tok = await fetchSfToken();
      afAccessTokenRef.current = tok.access_token;
    }
    return afAuth.sessionId; // may be null; unused
  }, [afAuth.sessionId]);

  /* ---------- Clear chat (clears all client sessions) ---------- */
  const clearChat = useCallback(async () => {
    try {
      if (afAuth.sessionId && afAccessTokenRef.current) {
        await endAfSession(afAccessTokenRef.current, afAuth.sessionId);
      }
    } catch {}
    setAfAuth({ sessionId: null });
    afSeqRef.current = 1;
    afAccessTokenRef.current = null;

    setSfAgentSessionId(null);
    localStorage.removeItem('sfAgentSessionId');

    setConversationId(null);
    localStorage.removeItem('conversationId');

    // Biz session clear
    setBizSessionId(null);
    localStorage.removeItem('bizSessionId');

    // Lyzr + Make thread clear
    setLyzrSessionId(null);
    localStorage.removeItem('lyzrSessionId');

    setMakeThreadId(null);
    localStorage.removeItem('makeThreadId');

    // overlays
    setVoiceOpen(false);
    setElevenOpen(false);
    setElevenReady(false);
    setElevenError('');
    if (elevenMORef.current) elevenMORef.current.disconnect();
    if (elevenIntervalRef.current) { clearInterval(elevenIntervalRef.current); elevenIntervalRef.current = null; }
    removeElevenFloaters();

    setMessages(initialMessages(strings,agentName));
  }, [afAuth.sessionId, strings, agentName]);

  /* ---------- Router ---------- */
  const routeMessage = async (msg) => {
    
    if (selectedAgent === 'Agentforce') {
      try {
        if (!afAccessTokenRef.current) {
          await getSfAccessTokenOnce(); // silent (no chat bubbles)
        }
        setIsLoading(true);

        const startMs = performance.now();
        const { replyText, sessionId } = await sendSfFlowMessage(
  afAccessTokenRef.current,
  msg,
  sfAgentSessionId,
  getDisplayMode()
);
        const elapsed = performance.now() - startMs;

        if (sessionId && sessionId !== sfAgentSessionId) {
          setSfAgentSessionId(sessionId);
          localStorage.setItem('sfAgentSessionId', sessionId);
        }

        setMessages(p => [
          ...p,
          {
            type:'bot',
            text: formatBotReply(replyText || strings.default_reply),
            timestamp: `${getTime()} (${formatLatency(elapsed)})`
          }
        ]);
      } catch (e) {
        console.error('SF Flow error:', e?.response?.status, e?.response?.data || e.message);
        setMessages(p => [
          ...p,
          {
            type:'bot',
            text: formatBotReply(`⚠️ ${e?.response?.data?.message || e.message || 'Salesforce request failed'}`),
            timestamp: `${getTime()} (${formatLatency(0)})`
          }
        ]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (selectedAgent === 'Lyzr Agent') {
      try {
        let sid = lyzrSessionId;
        if (!sid) {
          sid = genId(LYZR_AGENT_ID);
          setLyzrSessionId(sid);
          localStorage.setItem('lyzrSessionId', sid);
        }
        setIsLoading(true);

        const startMs = performance.now();
        const { replyText } = await sendLyzrMessage({ message: msg, sessionId: sid });
        const elapsed = performance.now() - startMs;

        setMessages(p => [
          ...p,
          {
            type:'bot',
            text: formatBotReply(replyText || strings.default_reply),
            timestamp: `${getTime()} (${formatLatency(elapsed)})`
          }
        ]);
      } catch (e) {
        console.error('Lyzr send error:', e?.response?.status, e?.response?.data || e.message);
        setMessages(p => [
          ...p,
          {
            type:'bot',
            text: formatBotReply('⚠️ Lyzr request failed.'),
            timestamp: `${getTime()} (${formatLatency(0)})`
          }
        ]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (selectedAgent === 'Make Agent') {
      try {
        let tid = makeThreadId;
        if (!tid) {
          tid = genId('thread');
          setMakeThreadId(tid);
          localStorage.setItem('makeThreadId', tid);
        }
        setIsLoading(true);
        const { replyText } = await sendMakeMessage({ message: msg, threadId: tid });
        setMessages(p => [
          ...p,
          { type:'bot', text: formatBotReply(replyText || strings.default_reply), timestamp: getTime() }
        ]);
      } catch (e) {
        console.error('Make send error:', e?.response?.status, e?.response?.data || e.message);
        setMessages(p => [
          ...p,
          { type:'bot', text: formatBotReply('⚠️ Make request failed.'), timestamp: getTime() }
        ]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Default → Biz Agent
    await handleBizResponse(msg);
  };

  /* ---------- Send (typed) ---------- */
  const handleSend = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    setInput('');
    setMessages((p) => [...p, { type: 'user', text: msg, timestamp: getTime() }]);
    await routeMessage(msg);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleButtonClick = (txt) => {
    setInput(txt);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  /* ---------- Upload ---------- */
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setMessages((p) => [...p, { type: 'user', text: `📎 ${files.map((f) => f.name).join(', ')}`, timestamp: getTime() }]);
    setIsUploading(true);
    try {
      const b64s = [];
      for (const f of files) b64s.push(await fileToBase64(f));
      await axios.post(uploadApiUrl, { conversationId, files: b64s }, { headers });
      setMessages((p) => [...p, { type: 'bot', text: formatBotReply('✅ Uploaded files.'), timestamp: getTime() }]);
    } catch (e) {
      console.error('Upload error:', e?.response?.status, e?.response?.data || e.message);
      setMessages((p) => [...p, { type: 'bot', text: formatBotReply('⚠️ Upload failed.'), timestamp: getTime() }]);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /* ---------- Cleanup Agentforce on unmount ---------- */
  useEffect(() => {
    return () => {
      const sid = afAuth.sessionId;
      const tok = afAccessTokenRef.current;
      if (sid && tok) endAfSession(tok, sid).catch(() => {});
    };
  }, [afAuth.sessionId]);

  /* ---------- End AF session when switching away ---------- */
  useEffect(() => {
    if (selectedAgent !== 'Agentforce' && afAuth.sessionId && afAccessTokenRef.current) {
      endAfSession(afAccessTokenRef.current, afAuth.sessionId).catch(() => {});
      setAfAuth({ sessionId: null });
      afSeqRef.current = 1;
    }
  }, [selectedAgent, afAuth.sessionId]);

  /* ---------- Voice: Deepgram flow ---------- */
  const onMicClick = async () => {
    setElevenOpen(false);
    setElevenReady(false);
    setElevenError('');
    if (elevenMORef.current) elevenMORef.current.disconnect();
    if (elevenIntervalRef.current) { clearInterval(elevenIntervalRef.current); elevenIntervalRef.current = null; }
    removeElevenFloaters();

    setVoiceOpen(true);
    setVoicePaused(false);
    voiceCtrlRef.current = null;
    setHasVoiceCtrl(false);

    const granted = await ensureMicPermission();
    if (!granted) {
      setMessages(p => [...p, { type: 'meta', text: '🎙️ Please allow microphone access to use voice.', timestamp: getTime() }]);
      setTimeout(() => setVoiceOpen(false), 1200);
      return;
    }

    try {
      const sendToAgent = async (transcript) => {
        setMessages(p => [...p, { type: 'user', text: transcript, timestamp: getTime() }]);
        try { await routeMessage(transcript); } finally { setVoiceOpen(false); }
      };

      const { didSpeak } = await voiceTurn({
        sendToAgent,
        captureOptions: { maxMs: 15000, silenceMs: 800, silenceThreshold: 0.02 },
        onController: (ctrl) => { voiceCtrlRef.current = ctrl; setHasVoiceCtrl(true); }
      });

      if (didSpeak === false) {
        setMessages(p => [...p, { type:'meta', text:'🗣️ I couldn’t hear anything. Tap the mic and try again.', timestamp: getTime() }]);
        setVoiceOpen(false);
      }
    } catch (e) {
      console.warn('Voice turn error:', e);
      setMessages(p => [...p, { type:'meta', text:'⚠️ Voice didn’t work this time. Please try again.', timestamp: getTime() }]);
      setVoiceOpen(false);
    } finally {
      voiceCtrlRef.current = null;
      setHasVoiceCtrl(false);
      setVoicePaused(false);
    }
  };

  /* ---------- ElevenLabs inline voice ---------- */
  const onElevenClick = async () => {
    setVoiceOpen(false);
    setVoicePaused(false);

    setElevenOpen(true);
    setElevenReady(false);
    setElevenError('');

    try {
      await ensureElevenScript();
      injectElevenHardKillCSS();
      removeElevenFloaters();

      requestAnimationFrame(() => setElevenReady(true));

      if (elevenMORef.current) elevenMORef.current.disconnect();
      const mo = new MutationObserver(() => adoptElevenFloatersInto(elevenFloatRef.current));
      mo.observe(document.body, { childList: true, subtree: true });
      elevenMORef.current = mo;

      if (elevenIntervalRef.current) clearInterval(elevenIntervalRef.current);
      elevenIntervalRef.current = setInterval(() => {
        adoptElevenFloatersInto(elevenFloatRef.current);
        injectElevenHardKillCSS();
      }, 500);
    } catch (e) {
      console.warn('ElevenLabs script load failed:', e);
      setElevenOpen(false);
      window.location.href = `https://elevenlabs.io/app/talk-to?agent_id=${ELEVEN_AGENT_ID}`;
    }
  };

  useEffect(() => {
    if (!elevenReady || !elevenOpen) return;
    const el = elevenElRef.current;
    if (!el) return;

    let started = false;
    const tryStart = () => {
      if (started) return;
      started = true;
      setTimeout(() => {
        try { el.start?.(); } catch {}
        try { el.call?.(); } catch {}
        try { el.setAttribute?.('open', ''); } catch {}
      }, 50);
    };

    try {
      el.addEventListener?.('ready', tryStart, { once: true });
      el.addEventListener?.('loaded', tryStart, { once: true });
      el.addEventListener?.('error', (e) => {
        setElevenError('Could not authorize the conversation. Check agent origin/permissions.');
        console.warn('ElevenLabs widget error:', e?.detail || e);
      });
    } catch {}

    const t = setTimeout(tryStart, 300);
    return () => clearTimeout(t);
  }, [elevenReady, elevenOpen]);

  useEffect(() => {
    if (!elevenOpen) {
      if (elevenMORef.current) elevenMORef.current.disconnect();
      if (elevenIntervalRef.current) { clearInterval(elevenIntervalRef.current); elevenIntervalRef.current = null; }
      removeElevenFloaters();
    } else {
      adoptElevenFloatersInto(elevenFloatRef.current);
    }
  }, [elevenOpen]);

  /* ---------- Layout helpers ---------- */
  const isCenterMode = agentParam === '2' && isOpen && isExpanded;
  // NEW: intro-only if agent=2 and there isn't any user message yet
const hasUserMsg = messages.some(m => m.type === 'user');
const isAgent2IntroOnly = isCenterMode && isAgent2 && !hasUserMsg;
  const hasStarted = messages.length > 0 || isLoading || isUploading;
  const targetVH = (isCenterMode && isExpanded) ? 94 : 70;
  const isAgent2Landing =
    agentParam === '2' && isCenterMode && messages.length === 0 && !isLoading && !isUploading;
  const tightHeaderStyle = isCenterMode ? { paddingTop: 6, paddingBottom: 6 } : undefined;

  const attachStyle = isExpanded ? { position: 'absolute', left: 12, bottom: 10 } : undefined;
  const sendStyle   = isExpanded ? { position: 'absolute', right: 12, bottom: 10 } : undefined;
  const canSend = input.trim().length > 0;
  const micStyle = isExpanded
    ? { position: 'absolute', right: canSend ? 52 : 12, bottom: 10, cursor: 'pointer' }
    : { cursor: 'pointer' };

  const popupStyle = isCenterMode
  ? (isAgent2IntroOnly
      ? { position: 'static', width: 'min(1000px, 96vw)', height: 'auto', maxHeight: 'none' }
      : {
          position: 'static',
          width: 'min(1000px, 96vw)',
          height: `${targetVH}vh`,
          maxHeight: `${targetVH}vh`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        })
  : undefined;


  const popup = (
<div className={`chatbot-popup ${isExpanded && !isAgent2IntroOnly ? 'expanded' : ''} ${isCenterMode ? 'standalone' : ''}`} style={popupStyle}>
      {/* Header */}
      <div
  className="chatbot-header"
  style={{
    ...tightHeaderStyle,
    background: theme?.headerBg || '#fff',
    color: theme?.headerText || '#000000'
  }}
>
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
   
    <span className="title">
      {strings?.header_title || brand?.appName || agentName}
    </span>
  </div>

  <div className="header-actions dropdown-anchor" ref={settingsRef}>

          <button className="icon-btn" onClick={clearChat} title="New chat" aria-label="New chat">
            <ClearChatIcon />
          </button>

          <button className="icon-btn" onClick={onElevenClick} title="Talk (ElevenLabs)" aria-label="Talk (ElevenLabs)">
            <ElevenIcon />
          </button>

          <button
  className="video-icon"
  title="Open Video Text Page"
  onClick={() => setShowVideoPage(true)}

  aria-haspopup="dialog"
  aria-expanded={showVideoPage}
>
<RecorderButtonPublic
  size={20}
  onClick={() => setShowVideoPage(true)}
/>
</button>


          {/* Agent options */}
          <button
            className="icon-btn gear-btn"
            title="Agent options"
            aria-haspopup="listbox"
            aria-expanded={showAgentDropdown}
            aria-controls="agent-dropdown"
            onClick={() => setShowAgentDropdown(v => !v)}
          >
            <GearIcon />
          </button>

          {showAgentDropdown && (
            <div id="agent-dropdown" role="listbox" className="settings-popover" aria-label="Select agent">
              {AGENT_OPTIONS.map((name) => (
                <button
                  key={name}
                  role="option"
                  aria-selected={selectedAgent === name}
                  className={`settings-item ${selectedAgent === name ? 'active' : ''}`}
                  onClick={async () => {
                    setSelectedAgent(name);
                    setShowAgentDropdown(false);
                    if (name === 'Agentforce') {
                      try {
                        await getSfAccessTokenOnce(); // silent prefetch
                      } catch (e) {
                        setAfAuth({ sessionId: null });
                        console.error('Token fetch failed:', e?.response?.data?.error || e.message);
                      }
                    }
                  }}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          <button className="icon-btn" onClick={() => setIsExpanded(v => !v)} title={isExpanded ? 'Restore' : 'Maximize'}>
            {isExpanded ? '🗗' : '⛶'}
          </button>
          <button className="icon-btn" onClick={() => setIsOpen(false)} title="Minimize">—</button>
        </div>
      </div>

      {/* ======== BODY + INPUT / INLINE OVERLAYS ======== */}
      {elevenOpen ? (
        <div
          className="voice-inline-overlay"
          role="dialog"
          aria-label="ElevenLabs Conversation"
          style={{ position:'relative', inset:'auto', flex:'1 1 0', minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden', background:'transparent' }}
        >
          {!!elevenError && (
            <div style={{position:'absolute', left:12, right:60, top:12, background:'#fee2e2', color:'#991b1b', border:'1px solid #fecaca', borderRadius:12, padding:'8px 10px', fontSize:13}}>
              {elevenError}
            </div>
          )}

          <div
            ref={elevenHostRef}
            className="eleven-host"
            style={{
              flex: '1 1 0',
              minHeight: 0,
              width: '100%',
              display: 'grid',
              gridTemplateRows: '1fr',
              placeItems: 'stretch',
              overflow: 'visible'
            }}
          >
            {!elevenReady ? (
              <div style={{ display:'grid', placeItems:'center', gap:12, color:'#fff' }}>
                <img src="/voice-loader.gif" alt="" onError={(e) => { e.currentTarget.src = '/voice-loader.gif'; }} style={{ width:180, height:180, objectFit:'contain' }} />
                <div style={{ fontWeight:700, fontSize:18 }}>Loading voice…</div>
              </div>
            ) : (
              <div style={{ width:'100%', height:'100%', display:'grid', placeItems:'stretch' }}>
                <elevenlabs-convai
                  ref={elevenElRef}
                  agent-id={ELEVEN_AGENT_ID}
                  inline
                  style={{ width:'100%', height:'100%', maxWidth:'100%', maxHeight:'100%', display:'block', overflow:'hidden' }}
                />
                <button
                  onClick={() => { setElevenOpen(false); setElevenReady(false); setElevenError(''); }}
                  className="voice-inline-btn"
                  aria-label="Close ElevenLabs"
                  title="Close"
                  style={{ position:'absolute', top:12, right:12, padding:'8px 12px', borderRadius: 9999, background:'#1f2937', color:'#fff', border:'1px solid rgba(255,255,255,0.12)', cursor:'pointer', zIndex:2 }}
                >
                  ✕
                </button>
                <div ref={elevenFloatRef} style={{ display:'none' }} />
              </div>
            )}
          </div>
        </div>
      ) : voiceOpen && USE_INLINE_VOICE_UI ? (
        <div className="voice-inline-overlay" role="dialog" aria-label="Listening" style={{ position:'relative', inset:'auto', flex:'1 1 0', minHeight:0, display:'flex', alignItems:'center', justifyContent:'center', overflow:'visible' }}>
          <button
            onClick={() => setVoiceOpen(false)}
            className="voice-inline-btn"
            aria-label="Close"
            title="Close"
            style={{ position:'absolute', top:12, right:12, padding:'8px 12px', borderRadius:9999, background:'#1f2937', color:'#fff', border:'1px solid rgba(255,255,255,0.12)', cursor:'pointer', zIndex:2 }}
          >
            ✕
          </button>

          <div style={{ display:'grid', gap:12, justifyItems:'center', textAlign:'center', color:'#000' }}>
            <img src="/voice-loader.gif" alt="" style={{ width:220, height:220, objectFit:'contain' }} />
            <div style={{ fontWeight:800, fontSize:20 }}>{voicePaused ? 'Paused' : 'Listening…'}</div>
            <div style={{ opacity:0.85, fontSize:14 }}>
              {voicePaused ? 'Tap resume to continue.' : 'Speak normally. I’ll stop when you pause.'}
            </div>
            <div style={{ marginTop:6 }}>
              <button
                disabled={!hasVoiceCtrl}
                onClick={async () => {
                  if (!hasVoiceCtrl || !voiceCtrlRef.current) return;
                  try {
                    if (!voicePaused) { await voiceCtrlRef.current.pause?.(); setVoicePaused(true); }
                    else { await voiceCtrlRef.current.resume?.(); setVoicePaused(false); }
                  } catch {}
                }}
                aria-label={voicePaused ? 'Resume recording' : 'Pause recording'}
                title={voicePaused ? 'Resume' : 'Pause'}
                style={{ padding:'10px 14px', borderRadius:9999, background:'#1f2937', color:'#fff', border:'1px solid rgba(255,255,255,0.12)', cursor: hasVoiceCtrl ? 'pointer' : 'not-allowed', width:44, height:44, display:'grid', placeItems:'center', fontSize:18, lineHeight:1, opacity: hasVoiceCtrl ? 1 : 0.5 }}
              >
                {hasVoiceCtrl ? (voicePaused ? '▶︎' : '⏸︎') : '…'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Body */}
          <div
            className={`chatbot-body ${isAgent2IntroOnly ? 'intro-only' : ''}`}
            style={
              isAgent2IntroOnly
                ? {
                    display: 'block',
                    flex: '0 0 auto',
                    maxHeight: 'none',
                    paddingTop: isCenterMode ? 10 : 16,
                  }
                : {
                    display: 'flex',
                    flex: '1 1 0',
                    minHeight: 0,
                    flexDirection: 'column',
                    paddingTop: isCenterMode ? 10 : 16,
                  }
            }
          >
            <div
              className={`chatbot-messages ${isAgent2IntroOnly ? 'intro-only' : ''}`}
              style={
                isAgent2IntroOnly
                  ? {
                      flex: '0 0 auto',
                      maxHeight: 'none',
                      overflow: 'visible',
                    }
                  : {
                      flex: 1,
                      overflowY: 'auto',
                      minHeight: 0,
                      display: isAgent2Landing ? 'none' : 'flex',
                    }
              }
            >
              {agentParam !== '2' && (
                <h2>
                  {strings.heading_question}{' '}
                  <span className="highlight">{strings.heading_bot_name}</span>{' '}
                  {strings.heading_suffix}
                </h2>
              )}
              
              {messages.map((m, i) => (
                <div key={i} className={`chatbot-message ${m.type}`}>
                  {m.type === 'bot' && (
                    <div className="chatbot-message bot">
                      <div className="bot-avatar-wrapper">
                        <img src={brand?.logoSrc || '/bot-avatar.png'}className="avatar" alt={brand?.botName || agentName || 'Bot'} />
                      </div>
                      <div className="bot-bubble-wrap">
                        <pre className="message-bubble bot-bubble" dangerouslySetInnerHTML={{ __html: m.text }} />
                        <div className="bot-timestamp-left">{m.timestamp}</div>
                      </div>
                    </div>
                  )}
                  {m.type === 'user' && (
                    <div className="chatbot-message user">
                      <div className="message-bubble user-bubble">{m.text}</div>
                      <div className="timestamp">{m.timestamp}</div>
                    </div>
                  )}
                  {m.type === 'bot-buttons' && (
                    <div className="chatbot-message bot-buttons">
                      {(Array.isArray(m.buttons) ? m.buttons : []).map((b, idx) => (
                        <button key={idx} onClick={() => handleButtonClick(b)}>{b}</button>
                      ))}
                    </div>
                  )}
                  {m.type === 'meta' && <div className="message-bubble centered">{m.text}</div>}
                </div>
              ))}
             {isLoading && (
  <ShimmerMessage brand={brand} agentName={agentName} theme={theme} />
)}

{isUploading && (
  <ShimmerMessage brand={brand} agentName={agentName} theme={theme} />
)}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <div className="chatbot-input">
            <input ref={fileInputRef} type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} />
            <div className={`input-bar ${isExpanded ? 'expanded' : ''}`} style={{ position: 'relative' }}>
              <button className="input-icon input-left-attach" onClick={() => fileInputRef.current?.click()} title="Attach" aria-label="Attach files" style={attachStyle}>
                <AttachIcon />
              </button>

              <textarea
                ref={textareaRef}
                className="input-text"
                placeholder={strings.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={rows}
                style={{
                  ...(isExpanded ? { paddingBottom: 34 } : null),
                  ...(isAgent2 && input.trim().length === 0
                    ? {
                        height: '36px',
                        maxHeight: '36px',
                        minHeight: '36px',
                        overflowY: 'hidden',
                      }
                    : {
                        overflowY: 'auto',
                      }),
                }}
              />

              <button className="input-icon dictate-btn" onClick={onMicClick} title="Talk" aria-label="Talk" style={micStyle}>
                <DictateIcon />
              </button>

              <button className={`send-btn ${canSend ? 'show' : ''}`} onClick={handleSend} disabled={!canSend} title="Send" aria-label="Send" style={sendStyle}>
                ➤
              </button>
            </div>
          </div>
        </>
      )}
      {/* ======== END INLINE WRAP ======== */}
    </div>
  );

  const OutsideTitle = () =>
    isCenterMode ? (
      <h2 className="standalone-title" style={{ position:'static', margin:0, transform:'none', textAlign:'center' }}>
        {strings.heading_question}{' '}
        <span className="highlight">{strings.heading_bot_name}</span>{' '}
        {strings.heading_suffix}
      </h2>
    ) : null;

  return (
    <>
      {!isAgent2 && (
        <button className="chatbot-toggle-button" onClick={() => setIsOpen(v => !v)}>
          <img src={brand?.logoSrc || '/bot-avatar.png'} alt={brand?.botName || agentName || 'Bot'} className="toggle-icon" />
          {strings.button_label}
        </button>
      )}

      {isOpen && (
        agentParam === '2' && isExpanded ? (
          <div className="standalone-center" style={{ marginTop:0, display:'grid', placeItems: 'center', minHeight:'90dvh' }}>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:20 }}>
              <OutsideTitle />
              {popup}
            </div>
          </div>
        ) : popup
      )}

{/* VideoTextPageDID overlay */}
{showVideoPage && (
  <div className="video-page-overlay">
    <div className="video-page-container">
      
      <VideoTextPageDID onClose={() => setShowVideoPage(false)} />
    </div>
    <button className="close-video-btn" onClick={() => setShowVideoPage(false)}>
        ✕
      </button>
  </div>
)}


      
    </>
  );
};

export default Chatbot;
